// DashboardItem.tsx
import React from "react";
import { DashboardItem as DashboardItemType } from "./Types";
import {
  getPadding,
  getBorderClasses,
  getBorderRadius,
  getTextSize,
  getIconSize,
} from "./DashboardUtils";
import { useAccountStore } from "../../store/useAccountStore";

interface DashboardItemProps {
  item: DashboardItemType;
  windowWidth: number;
  index: number;
}

// const DashboardItem: React.FC<DashboardItemProps> = ({ item, windowWidth, index }) => {
//     return (
//         <div
//             key={index}
//             className="absolute cursor-pointer transform hover:scale-105 active:scale-95 transition-all duration-300 z-20 group touch-manipulation"
//             style={{
//                 left: item.position.left,
//                 right: item.position.right,
//                 top: item.position.top,
//             }}
//         >
//             <div
//                 className={`bg-white backdrop-blur-sm hover:shadow-lg transition-all duration-300 group-hover:bg-green-50 shadow-[0px_2px_8px_0px_rgba(0,0,0,0.15)] ${getPadding(windowWidth)} ${getBorderClasses(item.title)} border-navgreen`}
//                 style={{
//                     borderRadius: `${getBorderRadius(windowWidth)} ${getBorderRadius(windowWidth)} ${getBorderRadius(windowWidth)} 0`
//                 }}
//             >
//                 <div className="flex items-center justify-center min-w-0">
//                     <span className={`${getTextSize(windowWidth)} text-navgreen group-hover:text-green-700 transition-colors duration-300 text-center font-medium leading-tight`}>
//                         {item.title === "Sales Portal" || item.title === "Workflow" ? (
//                             <span className="flex items-center gap-1 flex-wrap justify-center">
//                                 <span className="whitespace-nowrap text-center">{item.title}</span>
//                                 <span className={getIconSize(windowWidth)}>{item.icon}</span>
//                             </span>
//                         ) : (
//                             <span className="flex items-center gap-1 flex-wrap justify-center">
//                                 <span className={getIconSize(windowWidth)}>{item.icon}</span>
//                                 <span className="whitespace-nowrap text-center">{item.title}</span>
//                             </span>
//                         )}
//                     </span>
//                 </div>
//             </div>
//         </div>
//     );
// };

const encodeSSO = (str: string) =>
  btoa(str).replace(/\+/g, "-").replace(/\//g, "_").replace(/=/g, "~");

// Generate random string (matching PHP createRandomPassword function)
const createRandomPassword = () => {
  const chars = "abcdefghijkmnopqrstuvwxyz023456789";
  let pass = '';
  for (let i = 0; i <= 4; i++) {
    const num = Math.floor(Math.random() * 33);
    pass += chars[num];
  }
  return pass;
};

// Function to get final link
const getFinalLink = (item: any, username: string, password: string, email: string) => {
  if (!item?.sso) return item.link;

  const uname = encodeSSO(username);
  const pass = encodeSSO(password);

  // Special case for SALES PORTAL and SRN/POD PORTAL (email encoding format)
  // Note: Uses PLAIN base64 encoding (no character replacement) - matching PHP line 41
  if (item.sso === "emailenc") {
    const testrand = createRandomPassword();

    // Use email if available, otherwise construct from username
    let emailToEncode = email || username;

    // If email doesn't contain @, append the domain (matching PHP session username_ad format)
    if (!emailToEncode.includes('@')) {
      if(useAccountStore.getState().user?.company==='aosmith-india'){
        emailToEncode = `${emailToEncode}@aosmithindia.com`;

      } else {
        emailToEncode = `${emailToEncode}@aosmith.com`;

    }
  }

    const rawString = `${emailToEncode}|${testrand}`;
    const emailid_enc = btoa(rawString); // Plain base64, NO character replacement

    console.log("=== SSO Debug for", item.title, "===");
    console.log("Email from store:", email);
    console.log("Username from store:", username);
    console.log("Final email used:", emailToEncode);
    console.log("Random:", testrand);
    console.log("Raw String:", rawString);
    console.log("Base64 Encoded:", emailid_enc);
    console.log("Final URL:", `${item.link}sso/${emailid_enc}`);
    console.log("================");

    return `${item.link}sso/${emailid_enc}`;
  }

  // Special case for AR Tool
  if (item.title === "AR TOOL") {
    return `${item.link}sso.php?vobnd=${uname}&qbtt=${pass}`;
  }
  if(item.title==='EXPENSE PORTAL'){
    return `${item.link}?username=${uname}`;
  }

  // Default SSO format: /user/sso/<u>/<p>/<type>
  const type = encodeSSO(item.sso);
  return `${item.link}user/sso/${uname}/${pass}/${type}`;
};

const DashboardItem = ({ item }: { item: any }) => {
  const { user } = useAccountStore();
  const url = getFinalLink(item, user?.username || '', user?.password || '', user?.email || '');

  // Debug log for SALES PORTAL and SRN/POD
  if (item?.title === "SALES PORTAL" || item?.title === "SRN/POD PORTAL") {
    console.log(`${item?.title} Debug:`, {
      email: user?.email,
      username: user?.username,
      ssoType: item?.sso,
      link: item?.link,
      generatedURL: url
    });
  }

  // Special handling for WORKFLOW AUTOMATION (POST form submission)
  if (item?.sso === "postform") {
    const handleWorkflowClick = (e: React.MouseEvent) => {
      e.preventDefault();

      // Create a hidden form and submit it
      const form = document.createElement('form');
      form.method = 'POST';
      form.action = item.link;
      form.target = '_blank';

      const input = document.createElement('input');
      input.type = 'hidden';
      input.name = 'windows_login_id';
      input.value = btoa(user?.username || ''); // base64 encode username

      form.appendChild(input);
      document.body.appendChild(form);
      form.submit();
      document.body.removeChild(form);
    };

    return (
      <button
        onClick={handleWorkflowClick}
        className="px-3 py-4 flex border-navgreen border-r-2 transition-all duration-300 transform hover:-translate-y-1 border-b-2 overflow-hidden shadow-[0_0_2px_rgba(0,0,0,0.15)] hover:shadow-md rounded-md gap-2 items-center w-full text-left cursor-pointer"
      >
        {/* <div className="text-sm">{item?.icon}</div> */}
        <div className="flex-1">
          <p className="font-semibold font-poppins text-sm">{item?.title}</p>
          <p className="font-normal text-sm text-deepTeal line-clamp-2">
            {item?.description}
          </p>
        </div>
      </button>
    );
  }

  // Regular link for other items
  return (
    <a
      href={url}
      target="_blank"
      className="px-3 py-4 flex border-navgreen border-r-2 transition-all duration-300 transform hover:-translate-y-1 border-b-2 overflow-hidden shadow-[0_0_2px_rgba(0,0,0,0.15)] hover:shadow-md rounded-md gap-2 items-center"
    >
      {/* <div className="text-sm">{item?.icon}</div> */}
      <div className="flex-1">
        <p className="font-semibold font-poppins text-sm">{item?.title}</p>
        <p className="font-normal text-sm text-deepTeal line-clamp-2">
          {item?.description}
        </p>
      </div>
    </a>
  );
};
export default DashboardItem;
